import { useEffect, useMemo, useState } from 'react';
import type { DashboardData, OverviewBusiness, OverviewKeyword, OverviewPayload, PlaceData } from '../types';
import type { NavId } from './Sidebar';
import { getClientOverview } from '../lib/api';

// Client-facing Dashboard.
//
// This is the only screen most client-role users ever open, so it answers four
// questions in the order a business owner actually asks them:
//
//   1. How do my Google reviews compare with the shops beating me?
//   2. Am I growing faster or slower than them?
//   3. Can people actually find me on the map around here?
//   4. Do the AI assistants name me?
//
// Everything else — SoLV, ARP, SAIV, grid keys, the health score, the action
// worklist — stays on the Heatmap / AI Visibility / AI Insight tabs. Nothing on
// this page uses an internal abbreviation, and every figure carries a plain
// sentence under it saying what it means.
//
// Styling lives in index.css under `.ov-*` rather than the inline-style house
// pattern, because the layout is media-query driven (1 col → 2 at 900px → 3 at
// 1200px, with some cards always full width).

interface DashboardProps {
  data: DashboardData;
  onNav: (id: NavId) => void;
  readOnly?: boolean;
  onShare?: () => void;
  placeData?: PlaceData | null;
  onRefreshPlace?: () => Promise<void>;
  clientId: string;
  /** Supplied by the public share route, which embeds it; otherwise fetched. */
  overview?: OverviewPayload | null;
  /**
   * Tabs this viewer can actually reach. A share-link viewer has no AI
   * Visibility tab, so the drill-down links to it must not render — clicking
   * one would snap straight back to the Dashboard.
   */
  allowedNav?: NavId[];
}

export function Dashboard({
  data, onNav, readOnly = false, onShare, placeData, onRefreshPlace, clientId, overview: injected,
  allowedNav,
}: DashboardProps) {
  const { client } = data;

  const [overview, setOverview] = useState<OverviewPayload | null>(injected ?? null);
  const [loading, setLoading] = useState(!injected);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (injected) { setOverview(injected); setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    getClientOverview(clientId)
      .then(o => { if (!cancelled) { setOverview(o); setError(null); } })
      .catch(err => { if (!cancelled) setError(err instanceof Error ? err.message : String(err)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId, injected]);

  const title = placeData?.displayName ?? client.name;

  // Every drill-down goes through here, so a tab the viewer cannot open simply
  // renders as plain text instead of a dead link.
  const go = (id: NavId): (() => void) | null =>
    (!allowedNav || allowedNav.includes(id)) ? () => onNav(id) : null;

  return (
    <div className="ov ov-scroll">
      <div className="ov-inner">

        <div className="ov-id">
          <div>
            <h1 className="ov-id__t">{title}</h1>
            <div className="ov-id__m">
              {client.location}
              {placeData?.rating != null && <> · ★ {placeData.rating.toFixed(1)}</>}
              {placeData?.userRatingCount != null && <> · {fmt(placeData.userRatingCount)} reviews</>}
            </div>
          </div>
          {!readOnly && (
            <div className="ov-id__acts">
              {onRefreshPlace && <RefreshButton onRefresh={onRefreshPlace} />}
              {onShare && <button className="ov-btn" onClick={onShare}>Share dashboard</button>}
            </div>
          )}
        </div>

        <div className="ov-period">
          <div className="ov-period__bar">
            <div className="ov-period__cur">
              Latest results <em>· {periodLabel(overview)}</em>
            </div>
          </div>
          <div className="ov-period__note">
            Figures refresh on your scan schedule, not on the 1st of the month.
          </div>
        </div>

        {loading && <div className="ov-card ov-card--wide"><div className="ov-empty">Loading your results…</div></div>}

        {!loading && error && (
          <div className="ov-card ov-card--wide">
            <div className="ov-empty">
              <b>We could not load your results</b>
              {error}
            </div>
          </div>
        )}

        {!loading && !error && overview && (
          <>
            <TrafficSection overview={overview} go={go} />
            <ReviewSection overview={overview} go={go} />
            <MapsSection overview={overview} go={go} />
            <AiSection overview={overview} go={go} />
            <SummaryCard overview={overview} name={title} />
          </>
        )}
      </div>
    </div>
  );
}

// ── Traffic ──────────────────────────────────────────────────────────────────
//
// Two very different things sit under one heading, and the copy keeps them
// apart on purpose:
//
//   • What Google reports actually happened — views, direction requests, calls,
//     website clicks. This is the only measured traffic in GBI, and it needs a
//     connected Google account. Until then the card is an invitation, not a
//     number, because a placeholder figure here would be read as real.
//   • How many people search for what this business sells. That is market size,
//     not visits, and it says so.

function TrafficSection({ overview, go }: { overview: OverviewPayload; go: Go }) {
  const t = overview.traffic;
  const g = t.gbp;

  return (
    <>
      <SectionHead title="Your traffic" go={go('heatmap')} label="See where you show up" short="Where you show up" />

      {/* real, Google-reported activity */}
      <div className="ov-card ov-card--wide">
        <div className="ov-card__head">
          <span className="ov-card__label">Seen on Google</span>
          {g && !g.partial && (
            <span className="ov-chip ov-chip--hi"><b>{monthLabel(g.month)}</b></span>
          )}
        </div>

        {!t.connected ? (
          <div className="ov-connect">
            <div className="ov-connect__b">See how many people actually found you</div>
            <p>
              Google can tell us how many people saw this business on Maps and in Search last
              month, how many asked for directions, and how many tapped to call. It needs your
              Google Business Profile connected once — after that it updates on its own.
            </p>
            <span className="ov-connect__hint">Ask your YEA account manager to connect it.</span>
          </div>
        ) : !g ? (
          <EmptyBlock
            title="Connected — first figures on the way"
            body="Google publishes a few days behind, so the first full month appears here shortly."
          />
        ) : (
          <>
            <div className="ov-big ov-big--navy">{fmt(g.impressions)}</div>
            <div className="ov-said">times people saw you on Google</div>
            <div className="ov-when">
              {monthLabel(g.month)}{g.partial ? ' (still running)' : ''} · {fmt(g.maps)} on Maps
              {' · '}{fmt(g.search)} in Search
            </div>
            <Delta now={g.impressions} before={g.impressionsPrev} noun="views" />
            {g.series.length > 1 && <Spark points={g.series.map(p => p.impressions)} />}

            <div className="ov-stats" style={{ marginTop: 18 }}>
              <Stat n={fmt(g.directions)} label="asked for directions" go={null} />
              <Stat n={fmt(g.calls)} label="tapped to call you" go={null} />
              <Stat n={fmt(g.websiteClicks)} label="clicked through to your site" go={null} />
              <Stat n={fmt(g.actions)} label="did something after seeing you" tone="hero" go={null} />
            </div>

            <div className="ov-note">
              These are Google's own figures for your profile — not an estimate. "Did something"
              counts everyone who asked for directions, called, messaged, booked, or visited your
              website after your profile came up.
            </div>
          </>
        )}
      </div>

      {/* market size */}
      {t.demand && t.demand.monthlySearches > 0 && (
        <div className="ov-card ov-card--wide">
          <div className="ov-card__head">
            <span className="ov-card__label">How many people are looking</span>
            {t.demand.coveragePct != null && (
              <span className={`ov-chip ${t.demand.coveragePct >= 50 ? 'ov-chip--hi' : 'ov-chip--mid'}`}>
                <b>{t.demand.coveragePct}</b><span>% reached</span>
              </span>
            )}
          </div>

          <div className="ov-big ov-big--navy">{fmt(t.demand.monthlySearches)}</div>
          <div className="ov-said">searches a month for what you do</div>
          <div className="ov-when">
            Across your {t.demand.keywords.length} tracked
            {t.demand.keywords.length === 1 ? ' keyword' : ' keywords'}
            {t.demand.locationName ? ` in ${t.demand.locationName}` : ''}
          </div>

          {t.demand.estimatedReach != null && (
            <div className="ov-stats" style={{ marginTop: 18 }}>
              <Stat n={fmt(t.demand.estimatedReach)} label="of those searches can find you today"
                    tone="hero" go={go('heatmap')} />
              <Stat n={fmt(Math.max(0, t.demand.monthlySearches - t.demand.estimatedReach))}
                    label="cannot find you yet" tone="bad" go={go('heatmap')} />
            </div>
          )}

          <div className="ov-kws">
            {t.demand.keywords.slice(0, 6).map(k => (
              <RowLink go={go('heatmap')} className="ov-kw" key={k.keyword}>
                <div className="ov-kw__body">
                  <div className="ov-kw__term">{k.keyword}</div>
                  <div className="ov-kw__meta">
                    {k.searchVolume != null
                      ? `${fmt(k.searchVolume)} searches a month`
                      : 'search volume not published for this term'}
                  </div>
                </div>
              </RowLink>
            ))}
          </div>

          <div className="ov-note">
            This is how big the market is, not how many people visited you — it is the number of
            searches happening every month for the terms you are tracked on. The share you reach
            is your map coverage applied to it.
          </div>
        </div>
      )}
    </>
  );
}

// ── Reviews ──────────────────────────────────────────────────────────────────

function ReviewSection({ overview, go }: { overview: OverviewPayload; go: Go }) {
  const r = overview.reviews;
  const withTotals = useMemo(
    () => r.businesses.filter(b => b.totalReviews != null)
      .sort((a, b) => (b.totalReviews ?? 0) - (a.totalReviews ?? 0)),
    [r.businesses],
  );
  const withPace = useMemo(
    () => r.businesses.filter(b => b.monthlyPace != null)
      .sort((a, b) => (b.monthlyPace ?? 0) - (a.monthlyPace ?? 0)),
    [r.businesses],
  );
  const withQuality = useMemo(
    () => r.businesses.filter(b => b.effectivePct != null)
      .sort((a, b) => (b.effectivePct ?? 0) - (a.effectivePct ?? 0)),
    [r.businesses],
  );

  const me = r.businesses.find(b => b.isClient) ?? null;
  const fastestRival = withPace.find(b => !b.isClient) ?? null;

  return (
    <>
      <SectionHead title="Google reviews" go={go('analysis')} label="See what we're doing about it" short="What we're doing" />

      {/* total reviews */}
      <div className="ov-card ov-card--wide">
        <div className="ov-card__head">
          <span className="ov-card__label">Total reviews · you vs the shops near you</span>
          {r.clientRankByTotal != null && (
            <span className={`ov-chip ${r.clientRankByTotal === 1 ? 'ov-chip--hi' : 'ov-chip--mid'}`}>
              <b>#{r.clientRankByTotal}</b><span>of {withTotals.length}</span>
            </span>
          )}
        </div>

        {withTotals.length === 0 ? (
          <EmptyBlock
            title="No review data yet"
            body="We collect this on your first full scan. It will appear here automatically."
          />
        ) : (
          <>
            <HBars
              rows={withTotals}
              value={b => b.totalReviews ?? 0}
              format={v => fmt(v)}
              leadFirst
            />
            <div className="ov-gap">
              {me?.totalReviews != null && (
                <>
                  <div className="ov-gap__i">
                    <div className="ov-gap__n" style={{ color: '#2F74B5' }}>{fmt(me.totalReviews)}</div>
                    <div className="ov-gap__l">Your reviews</div>
                  </div>
                  <div className="ov-gap__sep" />
                </>
              )}
              {r.gapToLeader != null && (
                <>
                  <div className="ov-gap__i">
                    <div className="ov-gap__n" style={{ color: '#C13A70' }}>−{fmt(r.gapToLeader)}</div>
                    <div className="ov-gap__l">Behind {shortName(withTotals[0].name)}</div>
                  </div>
                  <div className="ov-gap__sep" />
                </>
              )}
              {r.leadOverNext != null && (
                <div className="ov-gap__i">
                  <div className="ov-gap__n" style={{ color: '#0B7F6C' }}>+{fmt(r.leadOverNext)}</div>
                  <div className="ov-gap__l">Ahead of the next shop</div>
                </div>
              )}
            </div>
            <div className="ov-note">
              The businesses you are measured against are the ones that keep appearing above you in
              local Google Maps searches.
            </div>
          </>
        )}
      </div>

      {/* monthly growth */}
      <div className="ov-card ov-card--wide">
        <div className="ov-card__head">
          <span className="ov-card__label">New reviews per month · you vs the shops near you</span>
          {r.clientRankByPace != null && withPace.length > 1 && (
            <span className={`ov-chip ${r.clientRankByPace === 1 ? 'ov-chip--hi' : r.clientRankByPace <= 2 ? 'ov-chip--mid' : 'ov-chip--lo'}`}>
              <b>#{r.clientRankByPace}</b><span>of {withPace.length}</span>
            </span>
          )}
        </div>

        {withPace.length === 0 ? (
          <EmptyBlock
            title="Growth data is still being collected"
            body="Month-by-month review counts arrive with your first full report run."
          />
        ) : (
          <>
            <HBars
              rows={withPace}
              value={b => b.monthlyPace ?? 0}
              format={v => `${round1(v)}`}
              suffix="/mo"
              threatFirst={!!(me?.monthlyPace != null && fastestRival?.monthlyPace != null
                && fastestRival.monthlyPace > me.monthlyPace)}
            />
            <div className="ov-gap">
              {me?.monthlyPace != null && (
                <>
                  <div className="ov-gap__i">
                    <div className="ov-gap__n" style={{ color: '#2F74B5' }}>{round1(me.monthlyPace)}</div>
                    <div className="ov-gap__l">You add per month</div>
                  </div>
                  <div className="ov-gap__sep" />
                </>
              )}
              {fastestRival?.monthlyPace != null && !fastestRival.isClient && (
                <>
                  <div className="ov-gap__i">
                    <div className="ov-gap__n" style={{ color: '#C13A70' }}>{round1(fastestRival.monthlyPace)}</div>
                    <div className="ov-gap__l">{shortName(fastestRival.name)} adds per month</div>
                  </div>
                  <div className="ov-gap__sep" />
                </>
              )}
              {r.overtakeMonths != null ? (
                <div className="ov-gap__i">
                  <div className="ov-gap__n" style={{ color: '#C13A70' }}>~{Math.round(r.overtakeMonths)}</div>
                  {/* overtakeRival is always set alongside overtakeMonths, so the
                      verb can agree with a named singular business. */}
                  <div className="ov-gap__l">Months until {shortName(r.overtakeRival ?? 'a rival')} passes you</div>
                </div>
              ) : r.catchUpMonths != null ? (
                <div className="ov-gap__i">
                  <div className="ov-gap__n" style={{ color: '#0B7F6C' }}>~{Math.round(r.catchUpMonths)}</div>
                  <div className="ov-gap__l">Months until you pass {shortName(r.catchUpRival ?? 'them')}</div>
                </div>
              ) : null}
            </div>

            {r.overtakeMonths != null && r.overtakeRival && (
              <div className="ov-note ov-note--flag">
                {r.overtakeRival} is behind you on total reviews but collecting them faster. At this
                pace they overtake you in about {Math.round(r.overtakeMonths)} month
                {Math.round(r.overtakeMonths) === 1 ? '' : 's'}. This is the most important number on
                this page.
              </div>
            )}
            {r.overtakeMonths == null && r.catchUpMonths != null && r.catchUpRival && (
              <div className="ov-note ov-note--flag" style={{ background: '#DCF4ED', color: '#0B7F6C' }}>
                You are collecting reviews faster than {r.catchUpRival}. Keep this pace and you pass
                them in about {Math.round(r.catchUpMonths)} month
                {Math.round(r.catchUpMonths) === 1 ? '' : 's'}.
              </div>
            )}
            <div className="ov-note">
              Averaged over the last complete months. The month still running is not counted, because
              a half-finished month always looks like a collapse.
            </div>
          </>
        )}
      </div>

      {/* quality */}
      <div className="ov-card ov-card--wide">
        <div className="ov-card__head">
          <span className="ov-card__label">Review quality · reviews that actually count</span>
          {me?.effectivePct != null && (
            <span className={`ov-chip ${me.effectivePct >= 80 ? 'ov-chip--hi' : me.effectivePct >= 60 ? 'ov-chip--mid' : 'ov-chip--lo'}`}>
              <b>{Math.round(me.effectivePct)}</b><span>%</span>
            </span>
          )}
        </div>

        {me?.effectivePct == null ? (
          <EmptyBlock
            title="Review quality is still being collected"
            body="We read your newest reviews and work out how many carry a photo or a real description."
          />
        ) : (
          <>
            <div className="ov-big ov-big--pos">{Math.round(me.effectivePct)}<span className="unit">%</span></div>
            <div className="ov-said">of your reviews actually count</div>
            <div className="ov-when">
              Newest reviews sampled{r.capturedAt ? ` · ${dateLabel(r.capturedAt)}` : ''}
            </div>
            {withQuality.length > 1 && (
              <HBars rows={withQuality} value={b => b.effectivePct ?? 0} format={v => `${Math.round(v)}`} suffix="%" />
            )}
            <div className="ov-note">
              An effective review has a photo or a real written description — the kind Google and AI
              can read and quote. A star with no words does almost nothing for your ranking.
              {me.photoPct != null && ` ${Math.round(me.photoPct)}% of yours carry photos`}
              {me.junkPct != null && ` and only ${Math.round(me.junkPct)}% are empty ratings`}.
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ── Maps ─────────────────────────────────────────────────────────────────────

function MapsSection({ overview, go }: { overview: OverviewPayload; go: Go }) {
  const m = overview.maps;
  const delta = m.previousFound != null ? m.found - m.previousFound : null;

  return (
    <>
      <SectionHead title="Google Maps" go={go('heatmap')} label="Open the heatmap" short="Heatmap" />

      <div className="ov-card ov-card--wide">
        <div className="ov-card__head">
          <span className="ov-card__label">Where you rank around your area</span>
          {m.total > 0 && (
            <span className="ov-chip ov-chip--mid"><b>{m.total}</b><span>searches</span></span>
          )}
        </div>

        {m.total === 0 ? (
          <EmptyBlock
            title="No map scan yet"
            body="Once your first scan runs we search Google Maps from points all around your shop and report where you show up."
          />
        ) : (
          <>
            <div className="ov-stats">
              <Stat n={m.rank1} label="spots where you rank #1"
                    tone={m.rank1 > 0 ? 'good' : 'bad'} go={go('heatmap')} />
              <Stat n={m.top3} label="times you land in the Top 3"
                    tone={m.top3 > 0 ? 'hero' : undefined} go={go('heatmap')} />
              <Stat n={m.coveragePct ?? 0} unit="%"
                    label={`of nearby spots can find you (${m.found} of ${m.total})`} go={go('heatmap')} />
              <Stat n={m.avgRank != null ? round1(m.avgRank) : '—'}
                    label="average position when you do appear" go={go('heatmap')} />
            </div>

            {delta != null && (
              <div className={`ov-delta ${delta > 0 ? 'ov-delta--pos' : delta < 0 ? 'ov-delta--neg' : 'ov-delta--navy'}`}>
                <span>{delta > 0 ? '▲' : delta < 0 ? '▼' : '—'}</span>
                <span>
                  {delta === 0
                    ? <>the same as the scan before</>
                    : <><b>{Math.abs(delta)} {delta > 0 ? 'more' : 'fewer'} spots</b> than the scan before</>}
                </span>
              </div>
            )}

            <div className="ov-note">
              We search Google Maps from {m.keywords[0]?.total ?? 9} points spread around your shop,
              once for each of your {m.keywords.length} keyword{m.keywords.length === 1 ? '' : 's'} —
              {' '}{m.total} searches in total — and count how many of them return your business.
            </div>
          </>
        )}
      </div>

      {m.keywords.length > 0 && (
        <div className="ov-card ov-card--wide">
          <div className="ov-card__head">
            <span className="ov-card__label">Strong and weak keywords</span>
          </div>
          <div className="ov-kws">
            {m.keywords.map(k => <KeywordRow key={k.keyword} kw={k} go={go('heatmap')} />)}
          </div>
          <div className="ov-key">
            <span><i style={{ background: '#0B7F6C' }} />Top 3</span>
            <span><i style={{ background: '#9DC2E6' }} />Position 4–9</span>
            <span><i style={{ background: '#D8DEE6' }} />Position 10+</span>
            <span><i style={{ background: '#E9ECF1' }} />Not found</span>
          </div>
          <div className="ov-note">
            Each block is one of the search points around your shop, laid out as they sit on the map —
            the top row is north of you, the middle row is your own street, the bottom row is south.
            The number is your position there.
          </div>
        </div>
      )}
    </>
  );
}

function KeywordRow({ kw, go }: { kw: OverviewKeyword; go: (() => void) | null }) {
  const badge = kw.strength === 'strong' ? 'STRONG' : kw.strength === 'mixed' ? 'MIXED' : 'WEAK';
  return (
    <RowLink go={go} className="ov-kw">
      <div className="ov-grid" style={{ gridTemplateColumns: `repeat(${kw.gridSize || 3}, 1fr)` }}>
        {kw.cells.map((rank, i) => (
          <div key={i} className={`ov-cell ${cellClass(rank)}`}>{rank ?? '·'}</div>
        ))}
      </div>
      <div className="ov-kw__body">
        <div className="ov-kw__term">{kw.keyword}</div>
        <div className="ov-kw__meta">
          Found at {kw.found} of {kw.total} spots
          {kw.rank1 > 0 && ` · #1 in ${kw.rank1}`}
          {kw.rank1 === 0 && kw.top3 > 0 && ` · Top 3 ${kw.top3} time${kw.top3 === 1 ? '' : 's'}`}
          {kw.avgRank != null && ` · usually around position ${Math.round(kw.avgRank)}`}
          {kw.found === 0 && ' · not showing anywhere yet'}
        </div>
      </div>
      <div className={`ov-kw__badge ov-kw__badge--${kw.strength}`}>{badge}</div>
    </RowLink>
  );
}

function cellClass(rank: number | null): string {
  if (rank == null) return 'ov-cell--none';
  if (rank <= 3) return 'ov-cell--t3';
  if (rank <= 9) return 'ov-cell--t10';
  return 'ov-cell--low';
}

// ── AI ───────────────────────────────────────────────────────────────────────

const ENGINE_LABEL: Record<string, string> = {
  chatgpt: 'ChatGPT', gemini: 'Gemini', gaio: 'Google AI Overview',
  grok: 'Grok', aimode: 'Google AI Mode',
};

function AiSection({ overview, go }: { overview: OverviewPayload; go: Go }) {
  const a = overview.ai;
  const deltaPct = a.pct != null && a.previousPct != null ? a.pct - a.previousPct : null;

  return (
    <>
      <SectionHead title="When people ask AI" go={go('scan')} label="Open AI visibility" short="AI visibility" />

      <div className="ov-card ov-card--wide">
        <div className="ov-card__head">
          <span className="ov-card__label">AI search</span>
          {a.tested > 0 && (
            <span className={`ov-chip ${a.mentioned === a.tested ? 'ov-chip--hi' : a.mentioned > 0 ? 'ov-chip--mid' : 'ov-chip--lo'}`}>
              <b>{a.mentioned}</b><span>/ {a.tested}</span>
            </span>
          )}
        </div>

        {a.tested === 0 ? (
          <EmptyBlock
            title="No AI check yet"
            body="We ask ChatGPT and Gemini the questions your customers ask, and record whether you get named."
          />
        ) : (
          <>
            <div className={`ov-big ${a.pct && a.pct > 0 ? 'ov-big--pos' : 'ov-big--navy'}`}>
              {a.pct ?? 0}<span className="unit">%</span>
            </div>
            <div className="ov-said">of AI answers we tested mention you</div>
            <div className="ov-when">
              {a.scanDate ? `Tested ${dateLabel(a.scanDate)} · ` : ''}
              {a.questions.length} question{a.questions.length === 1 ? '' : 's'} × {a.engines.length} engine
              {a.engines.length === 1 ? '' : 's'}
            </div>

            {deltaPct != null && (
              <div className={`ov-delta ${deltaPct > 0 ? 'ov-delta--pos' : deltaPct < 0 ? 'ov-delta--neg' : 'ov-delta--navy'}`}>
                <span>{deltaPct > 0 ? '▲' : deltaPct < 0 ? '▼' : '—'}</span>
                <span>
                  {deltaPct === 0
                    ? <>held at {a.pct}% since the last check</>
                    : <><b>{Math.abs(deltaPct)} points {deltaPct > 0 ? 'up' : 'down'}</b> on the last check</>}
                </span>
              </div>
            )}

            <div className="ov-engines">
              {a.engines.map(e => (
                <span key={e.platform} className={`ov-engine ${e.mentioned === 0 ? 'ov-engine--off' : ''}`}>
                  {ENGINE_LABEL[e.platform] ?? e.platform} {e.mentioned > 0 ? '✓' : '✗'} {e.mentioned} of {e.total}
                </span>
              ))}
            </div>

            <div className="ov-kws">
              {a.questions.map(q => {
                const named = q.byPlatform.some(p => p.mentioned);
                return (
                  <RowLink go={go('scan')} className="ov-q" key={q.keyword}>
                    <div className={`ov-q__pos ${named ? '' : 'ov-q__pos--miss'}`}>
                      {named && q.bestRank != null ? `#${Math.round(q.bestRank)}` : '—'}
                      <small>{named ? 'named' : 'not named'}</small>
                    </div>
                    <div className="ov-kw__body">
                      <div className="ov-kw__term">“{q.keyword}”</div>
                      <div className="ov-kw__meta">
                        {q.byPlatform.map(p => (
                          `${ENGINE_LABEL[p.platform] ?? p.platform} ${p.mentioned && p.arp != null ? `#${Math.round(p.arp)}` : '—'}`
                        )).join(' · ')}
                      </div>
                    </div>
                  </RowLink>
                );
              })}
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ── closing summary ──────────────────────────────────────────────────────────

// Written from the numbers above, deterministically. No model in the loop — the
// same data always produces the same sentence, so a client re-reading last
// month's dashboard sees exactly what they saw then.
function SummaryCard({ overview, name }: { overview: OverviewPayload; name: string }) {
  const { reviews: r, maps: m, ai: a } = overview;
  const me = r.businesses.find(b => b.isClient) ?? null;
  const parts: JSX.Element[] = [];

  if (a.pct != null && a.tested > 0) {
    parts.push(
      <span key="ai">
        {a.pct >= 100
          ? <>AI assistants named you in <span className="up">every answer</span> we tested</>
          : a.pct > 0
            ? <>AI assistants named you in <span className="up">{a.pct}%</span> of the answers we tested</>
            : <>AI assistants did <span className="dn">not name you</span> in any answer we tested</>}
        {'. '}
      </span>,
    );
  }
  if (m.total > 0) {
    parts.push(
      <span key="maps">
        On the map you show up in{' '}
        <span className={m.coveragePct != null && m.coveragePct >= 50 ? 'up' : 'dn'}>
          {m.found} of the {m.total}
        </span>{' '}
        spots we check{m.rank1 > 0
          ? <>, and you hold <b>{m.rank1}</b> first place{m.rank1 === 1 ? '' : 's'}</>
          : <>, and you hold <span className="dn">no first place</span> yet</>}
        {'. '}
      </span>,
    );
  }
  if (me?.totalReviews != null) {
    parts.push(
      <span key="rev">
        You have <b>{fmt(me.totalReviews)}</b> reviews
        {r.clientRankByTotal != null && <> — {ordinal(r.clientRankByTotal)} of the {r.businesses.filter(b => b.totalReviews != null).length} shops we track</>}
        {r.overtakeMonths != null && r.overtakeRival
          ? <>, and <b>{r.overtakeRival}</b> is collecting them <span className="dn">faster than you</span></>
          : r.catchUpMonths != null && r.catchUpRival
            ? <>, and you are closing on <b>{r.catchUpRival}</b> <span className="up">faster than they grow</span></>
            : null}
        {'.'}
      </span>,
    );
  }

  if (parts.length === 0) return null;

  return (
    <div className="ov-card ov-card--sum ov-card--wide">
      <div className="ov-card__head"><span className="ov-card__label">In one paragraph</span></div>
      <div className="ov-sum">{parts}</div>
      <div className="ov-note">
        Written straight from the figures above for {name}. Nothing here is an estimate.
      </div>
    </div>
  );
}

// ── drill-down plumbing ──────────────────────────────────────────────────────
//
// Every figure on this page is a summary of a tab that holds the detail, so the
// figure itself is the link. `go(tab)` returns null when the viewer cannot open
// that tab, and each helper below degrades to plain, unclickable markup — the
// alternative is a share-link viewer clicking "Top 3" and being bounced back
// here with no explanation.

type Go = (id: NavId) => (() => void) | null;

// `short` is the phone-width wording. The full label plus its arrow does not
// fit beside the section title on a 375px screen — it wrapped and left the
// arrow stranded on its own line — so the button keeps one line at every width
// and swaps to the shorter phrasing below 560px (see .ov-btn--sec in index.css).
function SectionHead({ title, go, label, short }: { title: string; go: (() => void) | null; label: string; short?: string }) {
  return (
    <div className="ov-sec">
      <b>{title}</b><i />
      {go && (
        <button className="ov-btn ov-btn--sec" onClick={go}>
          <span className="ov-btn__full">{label}</span>
          <span className="ov-btn__short">{short ?? label}</span>
          <span aria-hidden="true">→</span>
        </button>
      )}
    </div>
  );
}

function Stat({
  n, unit, label, tone, go,
}: {
  n: number | string; unit?: string; label: string;
  tone?: 'good' | 'bad' | 'hero'; go: (() => void) | null;
}) {
  const cls = `ov-stat${tone ? ` ov-stat--${tone}` : ''}${go ? ' ov-stat--link' : ''}`;
  const body = (
    <>
      <div className="ov-stat__n">{n}{unit && <span className="of">{unit}</span>}</div>
      <div className="ov-stat__l">{label}</div>
    </>
  );
  if (!go) return <div className={cls}>{body}</div>;
  return <button type="button" className={cls} onClick={go}>{body}</button>;
}

/** A row that is a link when it can be, and a plain row when it cannot. */
function RowLink({
  go, className, children,
}: { go: (() => void) | null; className: string; children: React.ReactNode }) {
  if (!go) return <div className={className}>{children}</div>;
  return (
    <button type="button" className={`${className} ov-row--link`} onClick={go}>
      {children}
    </button>
  );
}

// ── shared bits ──────────────────────────────────────────────────────────────

function HBars({
  rows, value, format, suffix, leadFirst, threatFirst,
}: {
  rows: OverviewBusiness[];
  value: (b: OverviewBusiness) => number;
  format: (v: number) => string;
  suffix?: string;
  /** Paint the top row navy — it is the business to beat, not a threat. */
  leadFirst?: boolean;
  /** Paint the top row magenta — it is a rival outgrowing the client. */
  threatFirst?: boolean;
}) {
  const max = Math.max(...rows.map(value), 1);
  return (
    <div className="ov-hbars">
      {rows.map((b, i) => {
        const v = value(b);
        const mod = b.isClient ? 'ov-hbar--own'
          : i === 0 && threatFirst ? 'ov-hbar--threat'
          : i === 0 && leadFirst ? 'ov-hbar--lead'
          : '';
        return (
          <div className={`ov-hbar ${mod}`} key={b.placeId || b.name}>
            <div className="ov-hbar__top">
              <div className="ov-hbar__name" title={b.name}>
                {b.name}
                {b.isClient && <span className="ov-tag">You</span>}
                {!b.isClient && i === 0 && threatFirst && <span className="ov-tag ov-tag--neg">Growing fast</span>}
              </div>
              <div className="ov-hbar__val">
                {format(v)}{suffix && <small>{suffix}</small>}
              </div>
            </div>
            <div className="ov-hbar__track">
              <div className="ov-hbar__fill" style={{ width: `${Math.max(2, (v / max) * 100)}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

function EmptyBlock({ title, body }: { title: string; body: string }) {
  return <div className="ov-empty"><b>{title}</b>{body}</div>;
}

function RefreshButton({ onRefresh }: { onRefresh: () => Promise<void> }) {
  const [busy, setBusy] = useState(false);
  return (
    <button
      className="ov-btn"
      disabled={busy}
      onClick={async () => {
        setBusy(true);
        try { await onRefresh(); } finally { setBusy(false); }
      }}
    >
      {busy ? 'Refreshing…' : '↻ Refresh Google profile'}
    </button>
  );
}

function Delta({ now, before, noun }: { now: number; before: number | null; noun: string }) {
  if (before == null) return null;
  const d = now - before;
  const cls = d > 0 ? 'pos' : d < 0 ? 'neg' : 'navy';
  return (
    <div className={`ov-delta ov-delta--${cls}`}>
      <span>{d > 0 ? '▲' : d < 0 ? '▼' : '—'}</span>
      <span>
        {d === 0
          ? <>the same as the month before</>
          : <><b>{fmt(Math.abs(d))} {d > 0 ? 'more' : 'fewer'} {noun}</b> than the month before</>}
      </span>
    </div>
  );
}

/** Same shape as the SEO Tracker's sparkline, so the two products match. */
function Spark({ points }: { points: number[] }) {
  if (points.length < 2) return null;
  const W = 320, H = 64, pad = 4;
  const max = Math.max(...points), min = Math.min(...points);
  const span = (max - min) || 1;
  const x = (i: number) => pad + i * ((W - pad * 2) / (points.length - 1));
  const y = (v: number) => H - pad - ((v - min) / span) * (H - pad * 2);
  const line = points.map((v, i) => `${i ? 'L' : 'M'}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(' ');
  const area = `${line} L${x(points.length - 1).toFixed(1)},${H} L${x(0).toFixed(1)},${H} Z`;
  return (
    <div className="ov-spark">
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
        <defs>
          <linearGradient id="ovspark" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="var(--s-accent)" stopOpacity=".20" />
            <stop offset="100%" stopColor="var(--s-accent)" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={area} fill="url(#ovspark)" />
        <path d={line} fill="none" stroke="var(--s-accent)" strokeWidth="2.5"
              strokeLinecap="round" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
      </svg>
    </div>
  );
}

function monthLabel(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return ym;
  return new Date(Date.UTC(y, m - 1, 1))
    .toLocaleDateString(undefined, { month: 'long', year: 'numeric', timeZone: 'UTC' });
}

// ── formatting ───────────────────────────────────────────────────────────────

function fmt(n: number): string { return n.toLocaleString(); }
function round1(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1);
}
function shortName(name: string): string {
  return name.length > 26 ? `${name.slice(0, 24)}…` : name;
}
function ordinal(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return n + (s[(v - 20) % 10] ?? s[v] ?? s[0]);
}
function dateLabel(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}
function periodLabel(overview: OverviewPayload | null): string {
  if (!overview) return '—';
  const stamps = [overview.maps.scanDate, overview.ai.scanDate, overview.reviews.capturedAt]
    .filter((n): n is number => n != null);
  if (stamps.length === 0) return 'no data yet';
  return `data to ${dateLabel(Math.max(...stamps))}`;
}
